import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import userManagementService from '../../services/userManagementService';
import { useStore } from '../../context/StoreContext';
import AppDataGrid from '../common/AppDataGrid';
import GridActionsCell from '../common/GridActionsCell';
import TableSearch from '../common/TableSearch';
import ActiveFilter from '../common/ActiveFilter';
import { useDataTable } from '../../hooks/useDataTable';
import { buildToggleAction, useCanUpdate } from '../common/activeToggle';

const UsersList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSuperAdmin } = useStore();
  const canToggle = useCanUpdate('users.update');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // Page, size, search, sort and the active filter live in the URL (/users?page=0&size=20&...).
  const table = useDataTable(userManagementService.getUsersPage, {}, {
    onError: () => toast.error(t('users.fetchError')),
  });
  const fetchUsers = table.refresh;

  const handleToggleActive = async (row) => {
    try {
      await userManagementService.toggleActive(row.id);
      toast.success(row.isActive === false ? t('common.activated') : t('common.deactivated'));
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.toggleError'));
    }
  };

  const handleDelete = async () => {
    try {
      await userManagementService.deleteUser(userToDelete.id);
      toast.success(t('users.deleteSuccess'));
      fetchUsers();
      setDeleteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const columns = [
    {
      field: 'avatar',
      headerName: '',
      width: 60,
      sortable: false,
      cellRenderer: (params) => (
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, my: 0.5 }}>
          {params.data.name?.charAt(0).toUpperCase()}
        </Avatar>
      )
    },
    { field: 'id', headerName: t('common.id'), width: 70 },
    { field: 'name', headerName: t('common.name'), width: 200 },
    { field: 'email', headerName: t('common.email'), width: 250 },
    ...(isSuperAdmin ? [{
      field: 'storeName',
      headerName: t('stores.store'),
      width: 200,
      cellRenderer: (params) => (
        params.value ? (
          <Chip label={params.value} color="info" size="small" />
        ) : (
          <Chip label={t('common.global')} color="default" size="small" />
        )
      )
    }] : []),
    {
      field: 'roleName',
      headerName: t('users.role'),
      width: 150,
      cellRenderer: (params) => (
        params.value ? (
          <Chip label={params.data.roleDisplayName || params.value} color="secondary" size="small" />
        ) : (
          <Chip label={t('users_ext.noRoleAssigned')} color="default" size="small" />
        )
      )
    },
    {
      field: 'globalRole',
      headerName: t('users.globalRole'),
      width: 150,
      cellRenderer: (params) => (
        <Chip
          label={params.value}
          color={params.value === 'SUPER_ADMIN' ? 'error' : 'primary'}
          size="small"
        />
      )
    },
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 120,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? t('common.active') : t('common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 150,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'view',
              icon: ViewIcon,
              title: t('common.details'),
              color: 'info',
              onClick: () => navigate(`/users/view/${params.data.id}`),
            },
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              onClick: () => navigate(`/users/edit/${params.data.id}`),
            },
            buildToggleAction({ row: params.data, onToggle: handleToggleActive, t, canToggle }),
            {
              key: 'delete',
              icon: DeleteIcon,
              title: t('common.delete'),
              color: 'error',
              onClick: () => {
                setUserToDelete(params.data);
                setDeleteDialogOpen(true);
              },
            },
          ]}
        />
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{t('users.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/create')}
        >
          {t('users.createUser')}
        </Button>
      </Box>

      <Card sx={{ mb: 2, p: 2 }}>
        <TableSearch
          placeholder={t('users.searchPlaceholder')}
          value={table.searchInput}
          onChange={table.handleSearchChange}
        />
      </Card>

      <ActiveFilter
        value={table.params.active || 'all'}
        onChange={(active) => table.updateParams({ active })}
        sx={{ mb: 2 }}
      />

      <Card>
        <AppDataGrid
          rows={table.data.content}
          columns={columns}
          loading={table.loading}
          paginationMode="server"
          rowCount={table.data.totalElements}
          paginationModel={table.paginationModel}
          onPaginationModelChange={table.handlePaginationModelChange}
          sortModel={table.sortModel}
          onSortModelChange={table.handleSortChange}
          pageSizeOptions={[10, 20, 50, 100]}
          getRowId={(row) => row.id}
        />
      </Card>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('users.deleteUser')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('users.deleteConfirm', { name: userToDelete?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersList;
